import { resolveCapitalGlobalDocumentLabel } from '@/features/quotes/utils/resolve-capital-global-id-type.util';
import { formatCurrency, formatDateToBrazil } from '@/shared/utils';
import { formatRegisterNumber } from '@/shared/utils/format-register-number';
import type {
  PolicyDetailsCoverage,
  PolicyDetailsCustomer,
  PolicyDetailsPaymentInfo,
  PolicyDetailsResponse,
  PolicyDetailsRiskProfile,
} from '@/services/policies/types';
import {
  hasRiskMembers,
  resolveRisksForCustomer,
  type EntityRisk,
} from '@/features/quotes/components/capital-global/steps/utils/resolve-risks-for-customer.util';
import { getCapitalGlobalInsuranceTypeLabel } from '@/shared/utils/capital-global-insurance-type.util';
import {
  isCapitalGlobalAssistanceOrCapitalization,
  isCapitalGlobalAssistanceUmbrella,
  isCapitalGlobalAssistanceVariant,
  isCapitalGlobalCapitalizationUmbrella,
  isCapitalGlobalCapitalizationVariant,
} from '@/shared/utils/capital-global-coverage-kind.util';

// ─── View model ──────────────────────────────────────────────────────────────

export type CgPolicyCoverageItem = {
  name: string;
  capital: string;
  premium: string;
};

export type CgPolicyCoverageGroup = {
  title: string;
  items: CgPolicyCoverageItem[];
};

export type CgPolicySimpleGroup = {
  title: string;
  items: string[];
};

export type CgPolicyLabeledItem = {
  label: string;
  value: string;
};

export type CgPolicyStipulant = {
  /** "Estipulante" para seq=0; "Subestipulante N" para seq>0. */
  label: string;
  riskItems: CgPolicyLabeledItem[];
  coverageGroups: CgPolicyCoverageGroup[];
  assistanceCapitalizationGroups: CgPolicySimpleGroup[];
};

export type CgPolicyPremiumCard = {
  totalValue: string;
  liquidValue: string;
  iofValue: string;
  periodicity: string;
  paymentMethod: string;
  product: string;
  policyNumber: string;
  dueDay: string;
  status: string;
};

export type CgPolicyStipulantHeader = {
  documentLabel: string;
  documentValue: string;
  socialName: string;
  email: string;
  phone: string;
};

export type CgPolicyEmission = {
  effectiveDate: string;
  expiryDate: string;
  issueDate: string;
  insuranceType: string;
};

export type CapitalGlobalPolicyView = {
  premium: CgPolicyPremiumCard;
  emission: CgPolicyEmission;
  stipulantHeader: CgPolicyStipulantHeader;
  stipulants: CgPolicyStipulant[];
  commercialization: CgPolicyCommercialization;
};

/** Percentuais de corretagem/agenciamento já formatados para exibição. */
export type CgPolicyCommercialization = {
  brokeragePercent: string;
  agencingPercent: string;
};

export type MapCapitalGlobalPolicyOptions = {
  productName: string;
  statusLabel: string;
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Códigos de forma de pagamento (CodForma) do InsureMO → descrição amigável. */
const PAYMENT_METHOD_MAP: Record<string, string> = {
  '1000013': 'Boleto',
  '1000025': 'Débito em Conta',
  '1000037': 'Cartão de Crédito',
  '1000049': 'PIX',
  '1000061': 'Transferência Bancária',
};

const PERIOD_LABELS: Record<number, string> = {
  1: 'Anual',
  2: 'Semestral',
  3: 'Quadrimestral',
  4: 'Trimestral',
  6: 'Bimestral',
  12: 'Mensal',
};

const PLACEHOLDER = '-';

function money(value?: number | null): string {
  if (value == null) return PLACEHOLDER;
  return formatCurrency(value);
}


/** "Morte" (exata, case-insensitive) é sempre básica por regra de negócio. */
function isBasicCoverage(coverage: PolicyDetailsCoverage): boolean {
  const name = (coverage.productElementDesc ?? '').trim();
  return /^morte$/i.test(name);
}

function resolveGroupProfileLabel(profile?: string): string {
  const map: Record<string, string> = {
    ESCALONADO: 'Escalonado',
    UNIFORME: 'Uniforme',
  };
  return map[(profile ?? '').toUpperCase()] ?? PLACEHOLDER;
}

function firstPaymentInfo(
  paymentInfo?: PolicyDetailsResponse['paymentInfo'],
): PolicyDetailsPaymentInfo | undefined {
  if (!paymentInfo) return undefined;
  return Array.isArray(paymentInfo) ? paymentInfo[0] : paymentInfo;
}

function resolveDueDay(payment?: PolicyDetailsPaymentInfo): string {
  const date = payment?.installmentList?.[0]?.installmentDate;
  if (!date) return PLACEHOLDER;
  // installmentDate é ISO (yyyy-MM-dd...) — extrai o dia sem depender de timezone.
  const match = String(date).match(/^\d{4}-\d{2}-(\d{2})/);
  if (match) return String(Number(match[1]));
  const day = new Date(date).getDate();
  return Number.isNaN(day) ? PLACEHOLDER : String(day);
}

// ─── Section mappers ───────────────────────────────────────────────────────────

function mapPremiumCard(
  policy: PolicyDetailsResponse,
  options: MapCapitalGlobalPolicyOptions,
): CgPolicyPremiumCard {
  const payment = firstPaymentInfo(policy.paymentInfo);
  const periodicity =
    payment?.installmentPeriodCount != null
      ? (PERIOD_LABELS[payment.installmentPeriodCount] ?? PLACEHOLDER)
      : PLACEHOLDER;
  const paymentMethod =
    (payment?.paymentMethod && PAYMENT_METHOD_MAP[payment.paymentMethod.trim()]) ||
    payment?.billingMethod ||
    PLACEHOLDER;

  return {
    totalValue: money(policy.grossPremium ?? policy.duePremium),
    liquidValue: policy.rawPremium != null ? money(policy.rawPremium) : PLACEHOLDER,
    iofValue: money(policy.iof ?? 0),
    periodicity,
    paymentMethod,
    product: options.productName,
    policyNumber: policy.externalPolicyNo || policy.policyNo || PLACEHOLDER,
    dueDay: resolveDueDay(payment),
    status: options.statusLabel,
  };
}

function mapEmission(policy: PolicyDetailsResponse): CgPolicyEmission {
  return {
    effectiveDate: formatDateToBrazil(policy.effectiveDate),
    expiryDate: formatDateToBrazil(policy.expiryDate),
    issueDate: formatDateToBrazil(
      policy.externalEffectiveDate || policy.issueDate || policy.effectiveDate,
    ),
    insuranceType: getCapitalGlobalInsuranceTypeLabel(policy.typeOfInsurance),
  };
}

function resolveStipulants(policy: PolicyDetailsResponse): PolicyDetailsCustomer[] {
  if (policy.customers && policy.customers.length > 0) {
    return [...policy.customers].sort(
      (a, b) => (a.sequenceNumber ?? 0) - (b.sequenceNumber ?? 0),
    );
  }
  return policy.customer ? [{ ...policy.customer, sequenceNumber: 0 }] : [];
}

function mapStipulantHeader(
  policy: PolicyDetailsResponse,
): CgPolicyStipulantHeader {
  const holder =
    resolveStipulants(policy).find(c => (c.sequenceNumber ?? 0) === 0) ??
    policy.customer ??
    {};

  return {
    documentLabel: resolveCapitalGlobalDocumentLabel(holder.documentType),
    documentValue:
      formatRegisterNumber(holder.cpf ?? holder.documentNumber ?? '') || PLACEHOLDER,
    socialName: holder.socialName || holder.name || PLACEHOLDER,
    email: holder.email || PLACEHOLDER,
    phone: holder.phone || holder.cellphone || PLACEHOLDER,
  };
}

function mapRiskItems(
  profiles: PolicyDetailsRiskProfile[],
): CgPolicyLabeledItem[] {
  if (!profiles.length) return [];

  const items: CgPolicyLabeledItem[] = [
    { label: 'Perfil do Grupo', value: resolveGroupProfileLabel(profiles[0]?.groupProfile) },
  ];

  const totalPartners = profiles.reduce(
    (sum, p) => sum + (p.partnersAndDirectors?.livesQuantity ?? 0),
    0,
  );
  const partnersCapital = profiles.reduce(
    (sum, p) =>
      sum +
      (p.partnersAndDirectors?.livesQuantity ?? 0) *
        (p.partnersAndDirectors?.individualCapital ?? 0),
    0,
  );
  const totalEmployees = profiles.reduce(
    (sum, p) => sum + (p.employees?.livesQuantity ?? 0),
    0,
  );
  const employeesCapital = profiles.reduce(
    (sum, p) =>
      sum + (p.employees?.livesQuantity ?? 0) * (p.employees?.individualCapital ?? 0),
    0,
  );

  if (totalPartners > 0) {
    items.push({ label: `${totalPartners} Sócio(s)`, value: money(partnersCapital) });
  }
  if (totalEmployees > 0) {
    items.push({
      label: `${totalEmployees} Colaborador(es)`,
      value: money(employeesCapital),
    });
  }

  const totalCapital = partnersCapital + employeesCapital;
  if (totalCapital > 0) {
    items.push({ label: 'Capital Total', value: money(totalCapital) });
  }

  return items;
}

function mapCoverageGroupsFromList(
  coverages: PolicyDetailsCoverage[],
  groupLabel?: string,
): CgPolicyCoverageGroup[] {
  // Sem preferir folha: cobertura com níveis de capital (`9300026` Auxílio
  // Funeral, `8200020`) vem como `isFinalLevelCt: 'N'` por ter benefício
  // aninhado, e na tela de seleção ela é uma LINHA DA TABELA como outra
  // qualquer. Preferindo folhas ela era descartada e sumia da apólice.
  const effective = coverages.filter(
    c => !isCapitalGlobalAssistanceOrCapitalization(c.productElementCode),
  );
  if (!effective.length) return [];

  const toItem = (c: PolicyDetailsCoverage): CgPolicyCoverageItem => ({
    name: c.productElementDesc || c.productElementCode || PLACEHOLDER,
    capital: money(c.sumInsured),
    premium: money(c.grossPremium),
  });

  const basics = effective.filter(isBasicCoverage).map(toItem);
  const optionals = effective.filter(c => !isBasicCoverage(c)).map(toItem);

  const prefix = groupLabel ? `${groupLabel} — ` : '';
  const groups: CgPolicyCoverageGroup[] = [];
  if (basics.length) groups.push({ title: `${prefix}Básicas`, items: basics });
  if (optionals.length) groups.push({ title: `${prefix}Opcionais`, items: optionals });
  return groups;
}

function mapCoverageGroupsForEntity(
  resolved: ReturnType<typeof resolveRisksForCustomer>,
): CgPolicyCoverageGroup[] {
  if (resolved.profile === 'ESCALONADO') {
    const groups: CgPolicyCoverageGroup[] = [];
    if (hasRiskMembers(resolved.partners)) {
      groups.push(
        ...mapCoverageGroupsFromList(resolved.partners?.coverages ?? [], 'Sócios'),
      );
    }
    if (hasRiskMembers(resolved.employees)) {
      groups.push(
        ...mapCoverageGroupsFromList(
          resolved.employees?.coverages ?? [],
          'Colaboradores',
        ),
      );
    }
    return groups;
  }
  return mapCoverageGroupsFromList(resolved.uniform?.coverages ?? []);
}

function mapAssistancesForEntity(
  resolved: ReturnType<typeof resolveRisksForCustomer>,
): CgPolicySimpleGroup[] {
  const sourceRisk =
    resolved.profile === 'ESCALONADO'
      ? (resolved.partners ?? resolved.employees)
      : resolved.uniform;

  const coverages = sourceRisk?.coverages ?? [];
  const groups: CgPolicySimpleGroup[] = [];

  // `EntityRisk` (tipo da cotação) não declara `productElementDesc` nos
  // benefícios, mas a resposta da apólice já vem enriquecida com a descrição.
  type CgBenefit = { productElementCode?: string; productElementDesc?: string };
  const benefitName = (b: CgBenefit) =>
    b.productElementDesc || b.productElementCode || '';

  /** Nome da própria cobertura — usado nas variantes numeradas. */
  const coverageName = (c: PolicyDetailsCoverage) =>
    c.productElementDesc || c.productElementCode || '';

  // Contêiner base: os BENEFÍCIOS são os itens. Variante numerada: o item é a
  // PRÓPRIA cobertura (lá o benefício é só o nível de capital, "R$ 7.000").
  const assistances = [
    ...coverages
      .filter(c => isCapitalGlobalAssistanceUmbrella(c.productElementCode))
      .flatMap(c => (c.benefits ?? []) as CgBenefit[])
      .map(benefitName),
    ...coverages
      .filter(c => isCapitalGlobalAssistanceVariant(c.productElementCode))
      .map(coverageName),
  ].filter(Boolean);
  if (assistances.length) groups.push({ title: 'Assistências', items: assistances });

  const capitalizations = [
    ...coverages
      .filter(c => isCapitalGlobalCapitalizationUmbrella(c.productElementCode))
      .flatMap(c => (c.benefits ?? []) as CgBenefit[])
      .map(benefitName),
    ...coverages
      .filter(c => isCapitalGlobalCapitalizationVariant(c.productElementCode))
      .map(coverageName),
  ].filter(Boolean);
  if (capitalizations.length) {
    groups.push({ title: 'Capitalização', items: capitalizations });
  }

  return groups;
}

function mapStipulants(policy: PolicyDetailsResponse): CgPolicyStipulant[] {
  const stipulants = resolveStipulants(policy);
  const risks = (policy.lobs?.[0]?.risks ?? []) as unknown as EntityRisk[];
  const riskProfiles = policy.riskProfileList ?? [];

  return stipulants.map((customer, index) => {
    const seq = customer.sequenceNumber ?? index;
    const label = seq === 0 ? 'Estipulante' : `Subestipulante ${seq}`;
    const resolved = resolveRisksForCustomer(seq, risks);

    return {
      label,
      riskItems: mapRiskItems(riskProfiles.filter(p => p.sequenceNumber === seq)),
      coverageGroups: mapCoverageGroupsForEntity(resolved),
      assistanceCapitalizationGroups: mapAssistancesForEntity(resolved),
    };
  });
}

/**
 * O agenciamento é informado como valor anual (12 parcelas); com menos
 * parcelas o exibido é o proporcional por parcela: divisor = 12 / parcelas.
 */
const AGENCING_DIVISOR_BY_INSTALLMENTS: Record<number, number> = {
  12: 1,
  6: 2,
  4: 3,
  3: 4,
  2: 6,
  1: 12,
};

function resolveInstallmentCount(policy: PolicyDetailsResponse): number | undefined {
  const payment = firstPaymentInfo(policy.paymentInfo);
  return payment?.installmentPeriodCount ?? payment?.installmentList?.length;
}

function formatAgencingPercent(
  raw: string | undefined,
  installmentCount: number | undefined,
): string {
  if (!raw) return PLACEHOLDER;

  const divisor =
    installmentCount != null
      ? AGENCING_DIVISOR_BY_INSTALLMENTS[installmentCount]
      : undefined;
  if (!divisor || divisor === 1) return `${raw}%`;

  const value = parseFloat(raw.replace(/\./g, '').replace(',', '.'));
  if (!Number.isFinite(value)) return `${raw}%`;

  const adjusted = (value / divisor).toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `${adjusted}%`;
}

/**
 * O corretor digita corretagem e agenciamento (o antigo combo de códigos saiu),
 * e o backend devolve os dois já em percent mascarado pt-BR ("20,00").
 * O agenciamento ainda é rateado pela quantidade de parcelas (regra acima).
 */
function mapCommercialization(
  policy: PolicyDetailsResponse,
): CgPolicyCommercialization {
  const { brokeragePercent, agencingPercent } = policy.commercialization ?? {};
  return {
    brokeragePercent: brokeragePercent ? `${brokeragePercent}%` : PLACEHOLDER,
    agencingPercent: formatAgencingPercent(
      agencingPercent,
      resolveInstallmentCount(policy),
    ),
  };
}

// ─── Main export ───────────────────────────────────────────────────────────────

export function mapPolicyDetailsToCapitalGlobalView(
  policy: PolicyDetailsResponse,
  options: MapCapitalGlobalPolicyOptions,
): CapitalGlobalPolicyView {
  return {
    premium: mapPremiumCard(policy, options),
    emission: mapEmission(policy),
    stipulantHeader: mapStipulantHeader(policy),
    stipulants: mapStipulants(policy),
    commercialization: mapCommercialization(policy),
  };
}
